import { format } from "date-fns";
import { useConfirm } from "material-ui-confirm";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { useDateFnsLocale } from "../../../utils/date-locale";
import { HolidayConflict } from "../types";
import { dateKeyToLocalDate, toDateKey } from "../utils/holiday-dates";

interface ThunkResult {
	meta?: { requestStatus?: string };
	payload?: { holidayConflicts?: HolidayConflict[] };
}

/**
 * Shifts/Create rejects a batch with 409 when a shift lands on a holiday the
 * supervisor hasn't confirmed (e.g. holidays changed after the warning was
 * shown). This dispatches the create, and on that 409 asks once and resubmits
 * with the flagged positions confirmed.
 */
const useHolidayConflictRetry = () => {
	const dispatch = useDispatch();
	const confirm = useConfirm();
	const { t } = useTranslation();
	const dateLocale = useDateFnsLocale();

	return useCallback(
		async (createAction: (confirmedIndexes: number[]) => unknown) => {
			const result = (await dispatch(
				createAction([]) as any
			)) as unknown as ThunkResult;
			const conflicts = result?.payload?.holidayConflicts;
			if (result?.meta?.requestStatus !== "rejected" || !conflicts?.length) {
				return;
			}
			const dates = [
				...new Set(
					conflicts.map((conflict) =>
						format(
							dateKeyToLocalDate(toDateKey(conflict.date)),
							"EEE, MMM d",
							{ locale: dateLocale }
						)
					)
				),
			].join(", ");
			try {
				await confirm({
					title: t("holidays_conflict-title", { count: conflicts.length }),
					content: t("holidays_conflict-body", {
						count: conflicts.length,
						dates,
					}),
					cancellationText: t("common_cancel"),
					// Plain text; the default confirm options style it as the
					// contained primary button
					confirmationText: t("holidays_conflict-confirm"),
				});
			} catch (err) {
				// Supervisor chose not to post on the holiday
				return;
			}
			dispatch(
				createAction(conflicts.map((conflict) => conflict.index)) as any
			);
		},
		[dispatch, confirm, t, dateLocale]
	);
};

export default useHolidayConflictRetry;
